/**
 * Above-canvas wallet + lunar-streak strip.
 *
 * Replaces the legacy absolutely-positioned `moonstone-hud` overlay that
 * sat in the canvas's top-right corner and could cover bricks in busy
 * sectors. Now lives in the layout flow ABOVE the canvas so nothing
 * occludes the game view — pilots can see every brick at all times.
 *
 * Surfaces:
 *   - ☾ Moonstone wallet chip (recoloured by the active skin palette)
 *   - Lunar Streak ×2 bonus countdown chip
 *   - Active moonstone-shop effect timers (Phase, Dilation, Plasma)
 *   - Shield-Armed marker
 *
 * Renders ONLY when at least one of those signals is non-zero, so the
 * strip silently disappears for fresh runs without state.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. The `hudTick` prop is
 * intentional — it's the canvas's internal "force re-render" tick so
 * the countdown timers tick down at ~30fps without each chip wiring
 * its own RAF.
 */
export function WalletStreakStrip({
  moonstones,
  skinMid,
  streakBonusLeft,
  shieldArmed,
  fxEntries,
  hudTick,
  paused,
  gameOver,
  won,
  isFullscreen,
}) {
  if (paused || gameOver || won) return null;
  const hasStreak = streakBonusLeft > 0;
  const hasShield = !!shieldArmed;
  const hasFx = (fxEntries || []).length > 0;
  // In windowed play we live in the layout flow ABOVE the canvas (no
  // overlap with bricks — the original user-reported issue). In FS the
  // canvas covers the viewport, so we pin to the top-right with
  // pointer-events-none so the strip remains visible without grabbing
  // input from the paddle.
  const wrapperClass = isFullscreen
    ? "fixed top-3 right-3 z-50 pointer-events-none flex flex-wrap items-center justify-end gap-2 max-w-[80vw]"
    : "w-full max-w-[800px] mb-2 flex flex-wrap items-center justify-end gap-2";
  return (
    <div className={wrapperClass} data-testid="moonstone-hud">
      <div
        className="px-2 py-1 border bg-black/70 font-mono-cosmic text-[11px] tracking-widest uppercase flex items-center gap-1.5"
        style={{
          borderColor: `${skinMid}b3`,
          color: skinMid,
          boxShadow: `0 0 10px ${skinMid}73`,
        }}
        data-testid="moonstone-hud-chip"
      >
        <span className="text-base leading-none">☾</span>
        <span className="tabular-nums" data-testid="moonstone-count">
          {moonstones}
        </span>
      </div>

      {hasStreak && (
        <div
          className="px-2 py-0.5 border border-[#ffd700]/80 bg-black/80 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffd700] tabular-nums animate-pulse"
          style={{ boxShadow: "0 0 10px rgba(255,215,0,0.5)" }}
          data-testid="moonstone-fx-streak"
        >
          STREAK x2 · {streakBonusLeft} LEFT
        </div>
      )}

      {hasShield && (
        <div
          className="px-2 py-0.5 border border-[#ffd700]/70 bg-black/70 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffd700]"
          data-testid="moonstone-fx-shield"
          style={{ boxShadow: "0 0 8px rgba(255,215,0,0.55)" }}
        >
          Shield Armed
        </div>
      )}

      {hasFx &&
        fxEntries.map(([id, label, color, ms]) => (
          <div
            key={id}
            className="px-2 py-0.5 border bg-black/70 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase tabular-nums"
            style={{
              borderColor: `${color}aa`,
              color,
              boxShadow: `0 0 8px ${color}55`,
            }}
            data-testid={`moonstone-fx-${id}`}
          >
            {label} {(ms / 1000).toFixed(1)}s
          </div>
        ))}

      <span className="hidden">{hudTick}</span>
    </div>
  );
}
